// Geometry and cell model for the Now Map sector chart.
// Everything is derived from VIEW_SIZE and RING_DEFS so there are no scattered magic coordinates.

export const VIEW_SIZE = 640
export const CENTER = VIEW_SIZE / 2
export const CENTER_RADIUS = 70

// Angle bands (degrees) per quadrant, ordered by intensity level (low → high).
export const QUADRANTS = {
    TR: [[330, 360], [300, 330], [270, 300]],
    TL: [[180, 210], [210, 240], [240, 270]],
    BL: [[150, 180], [120, 150], [90, 120]],
    BR: [[0, 30], [30, 60], [60, 90]],
}
export const Q_KEYS = ['TR', 'TL', 'BL', 'BR']

export const RING_DEFS = [
    { inner: 80, outer: 140, mid: 110, futureLabel: 'Next', pastLabel: 'Last' },
    { inner: 150, outer: 210, mid: 180, futureLabel: 'After', pastLabel: 'Before' },
    { inner: 220, outer: 280, mid: 250, futureLabel: 'Future', pastLabel: 'Past' },
]

export const LEVEL_LABELS = ['Low', 'Mid', 'High']

export const point = (r, angleDeg) => {
    const rad = (angleDeg * Math.PI) / 180
    return [CENTER + r * Math.cos(rad), CENTER + r * Math.sin(rad)]
}

export const sectorPath = (inner, outer, a0, a1) => {
    const p1 = point(outer, a0)
    const p2 = point(outer, a1)
    const p3 = point(inner, a1)
    const p4 = point(inner, a0)
    const f = n => n.toFixed(2)
    return `M ${f(p1[0])},${f(p1[1])} A ${outer},${outer} 0 0,1 ${f(p2[0])},${f(p2[1])} L ${f(p3[0])},${f(p3[1])} A ${inner},${inner} 0 0,0 ${f(p4[0])},${f(p4[1])} Z`
}

export const cellDescriptor = cell => {
    const ringName = cell.side === 'future' ? RING_DEFS[cell.ring].futureLabel : RING_DEFS[cell.ring].pastLabel
    return `${ringName} · ${LEVEL_LABELS[cell.level]} ${cell.valence === 'positive' ? 'positive' : 'negative'}`
}

export const itemsInCell = (items, cell) =>
    !cell
        ? []
        : items.filter(i => i.side === cell.side && i.ring === cell.ring && i.valence === cell.valence && i.level === cell.level)

export const cellsEqual = (a, b) =>
    !!a && !!b && a.side === b.side && a.ring === b.ring && a.valence === b.valence && a.level === b.level

// One entry per clickable sector: the cell it represents, its SVG path, and where its count label sits
// (label coordinates are the polar midpoint of the sector, in viewBox units).
export const sectorDefs = []
RING_DEFS.forEach((rd, ring) => {
    Q_KEYS.forEach(qKey => {
        const side = qKey === 'TR' || qKey === 'BR' ? 'future' : 'past'
        const valence = qKey === 'TR' || qKey === 'TL' ? 'positive' : 'negative'
        QUADRANTS[qKey].forEach((range, level) => {
            const midAngle = (range[0] + range[1]) / 2
            const [labelX, labelY] = point(rd.mid, midAngle)
            sectorDefs.push({
                id: `${ring}-${qKey}-${level}`,
                cell: { side, ring, valence, level },
                d: sectorPath(rd.inner, rd.outer, range[0], range[1]),
                labelX,
                labelY,
            })
        })
    })
})

// Ring name labels sit on the horizontal axis, at each ring's mid radius: future names to the right,
// past names to the left of the center.
export const ringLabels = []
RING_DEFS.forEach(rd => {
    ringLabels.push({ text: rd.futureLabel, x: CENTER + rd.mid, y: CENTER })
    ringLabels.push({ text: rd.pastLabel, x: CENTER - rd.mid, y: CENTER })
})

// Axis labels sit just outside the outermost ring (offsets account for text baseline).
const OUTERMOST = RING_DEFS[RING_DEFS.length - 1].outer
export const axisLabels = [
    { text: 'POSITIVE', x: CENTER, y: CENTER - OUTERMOST - 6 },
    { text: 'NEGATIVE', x: CENTER, y: CENTER + OUTERMOST + 12 },
]
